import { isNullOrUndefined } from "../../../../../../../tools";
import { IdProvider } from "../../../../../../services/idprovider.service";
import { ServerCommunicatorService } from "../../../../../../services/server-communicator.service";
import { UIUtils } from "../../../../../../services/uiutils.service";
import { Utils } from "../../../../../../services/utils.service";
import { DataContainer } from "../../../../../infrastructure/datacontainer";
import { DataContainerService } from "../../../../../infrastructure/datacontainer.service";
import { RequestTypes } from "../../../../../infrastructure/enums";
import { IPersistable } from "../../../../../infrastructure/IPersistable";
import { PayloadPacketFacade } from "../../../../../infrastructure/payloadpacket/payloadpacketfacade";
import { TransportData } from "../../../../../infrastructure/transportdata";
import { ValidationResultAccumulator } from "../../../../../infrastructure/validationresultaccumulator";
import { ValidationMessages, ValidationPatterns } from "../../../../constants";
import { BlockWithFetchRequest } from "./blockwithfetchrequest";

export class BlockProps {

 public created_date: string = '';
  public updated_date: string = '';
  public created_by: number = 0;
  public updated_by: number = 0;

  public id: number = 0;
  public name_en: string = '';
  public name_mr: string = '';
  public sub_district_code: number = 0;
  public state_id: number = 0;
  public readonly state_name_en: string = '';
  public readonly state_name_mr: string = '';
  public zone_id: number = 0;
  public readonly zone_name: string = '';

  public financial_year_id: number = 0;

  public district_id: number = 0;
  public readonly district_name_en: string = '';
  
  public sanctioned_no_of_asha_in_block: number = 0;
  public sanctioned_no_of_af_in_block: number = 0;
  
  public month: string = '';
  
  public readonly is_newly_created: boolean = false;


  private constructor(IsNewlyCreated: boolean) {
    this.is_newly_created = IsNewlyCreated;
  }

  public static Blank() {
    return new BlockProps(true);
  }
}

export class Block implements IPersistable<Block> {
  public static readonly mastertablename: string = 'block_master';

  private constructor(public readonly p: BlockProps, public readonly AllowEdit: boolean) {

  }

  public async EnsurePrimaryKeysWithValidValues(): Promise<void> {
    if (this.p.id === undefined || this.p.id === 0) {
      const newids = await IdProvider.GetInstance().GetNextEntityId();
      // const newids = await IdProvider.GetInstance().GetAllocateSingleIds();
      this.p.id = newids[0];
      if (this.p.id <= 0) throw new Error("Cannot assign Id. Please try again");
    }
  }

  public GetEditableVersion(): Block {
    let newBlock: BlockProps = Utils.GetInstance().DeepCopy(this.p);
    return Block.CreateInstance(newBlock, true);
  }

  public static CreateNewInstance() {
    return new Block(BlockProps.Blank(), true);
  }

  public static CreateInstance(data: any, allowEdit: boolean) {
    return new Block(data as BlockProps, allowEdit);
  }

  public CheckSaveValidity(_td: TransportData, vra: ValidationResultAccumulator): void {
    if (!this.AllowEdit) vra.add('', 'This object is not editable and hence cannot be saved.');
    
    if (this.p.state_id <= 0) vra.add('state_id', 'State cannot be blank.');
    if (this.p.zone_id <= 0) vra.add('zone_id', 'Zone cannot be blank.');
    if (this.p.district_id <= 0) vra.add('district_id', 'District cannot be blank.');
    
    // Validate Month (string but numeric)
    const monthNum = Number(this.p.month);
    if (!this.p.month || isNaN(monthNum) || monthNum < 1 || monthNum > 12) {vra.add('month', 'Please select a valid month.');}
    
    if (!this.p.name_en || this.p.name_en.trim() == '') {vra.add('Name', 'Block Name cannot be blank. ');}

     // ✔ Correct validation for compulsory + > 0
  if (!this.p.sanctioned_no_of_af_in_block || this.p.sanctioned_no_of_af_in_block <= 0) {vra.add('sanctioned_no_of_af_in_block', 'Sanctioned No. of AF must be greater than 0.');}
  if (!this.p.sanctioned_no_of_asha_in_block || this.p.sanctioned_no_of_asha_in_block <= 0) {vra.add('sanctioned_no_of_asha_in_block', 'Sanctioned No. of ASHA must be greater than 0.')} }


  public MergeIntoTransportData(td: TransportData) {
    DataContainerService.GetInstance().MergeIntoContainer(td.MainData, Block.mastertablename, this.p);
  }

  private static m_currentInstance: Block = Block.CreateNewInstance();

  public static GetCurrentInstance() {
    return Block.m_currentInstance;
  }

  public static SetCurrentInstance(value: Block) {
    Block.m_currentInstance = value;
  }

  // ********************************************
  public static cacheDataChangeLevel: number = -1;

  public static SingleInstanceFromTransportData(td: TransportData): Block {
    let dcs = DataContainerService.GetInstance();
    if (dcs.CollectionExists(td.MainData, Block.mastertablename)) {
      for (let data of dcs.GetCollection(td.MainData, Block.mastertablename)!.Entries) {
        return Block.CreateInstance(data, false);
      }
    }

    return null as any;
  }

  public static ListFromDataContainer(cont: DataContainer,
    filterPredicate: (arg0: any) => boolean = null as any,
    sortPropertyName: string = "Name"): Block[] {
    let result: Block[] = [];

    let dcs = DataContainerService.GetInstance();

    if (dcs.CollectionExists(cont, Block.mastertablename)) {
      let coll = dcs.GetCollection(cont, Block.mastertablename)!;
      let entries = coll.Entries;

      if (!isNullOrUndefined(filterPredicate)) entries = entries.filter(filterPredicate);

      if (sortPropertyName.trim().length > 0) {
        entries = Utils.OrderByPropertyName(entries, sortPropertyName);
      }

      for (let data of entries) {
        result.push(Block.CreateInstance(data, false));
      }
    }

    return result;
  }

  public static ListFromTransportData(td: TransportData): Block[] {
    return Block.ListFromDataContainer(td.MainData);
  }

  public static async FetchTransportData(req: BlockWithFetchRequest, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let tdRequest = req.FormulateTransportData();
    let pktRequest = PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);

    let tr = await ServerCommunicatorService.GetInstance().sendHttpRequest(pktRequest);
    if (!tr.Successful) {
      if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
      return null;
    }

    let tdResponse = JSON.parse(tr.Tag) as TransportData;
    return tdResponse;
  }

  // Function created For Trasaction Service filters 
      public static async FetchTransportDataForTransactionFilter(req: BlockWithFetchRequest, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
        let tdRequest = req.FormulateTransportData();
        let pktRequest = PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);
    
        let tr = await ServerCommunicatorService.GetInstance().sendHttpRequest(pktRequest);
        if (!tr.Successful) {
          if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
          return null;
        }
    
        let tdResponse = JSON.parse(tr.Tag) as TransportData;
        return tdResponse;
      }

  public static async FetchInstance(id: number, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new BlockWithFetchRequest();
    req.block_ids.push(id);

    let tdResponse = await Block.FetchTransportData(req, errorHandler) as TransportData;
    return Block.SingleInstanceFromTransportData(tdResponse);
  }

  public static async FetchList(req: BlockWithFetchRequest, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let tdResponse = await Block.FetchTransportData(req, errorHandler) as TransportData;
    return Block.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireList(errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new BlockWithFetchRequest();
    let tdResponse = await Block.FetchTransportData(req, errorHandler) as TransportData;
    return Block.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireListByDistrictRef(district_id: number, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new BlockWithFetchRequest();
    req.district_ids.push(district_id)
    let tdResponse = await Block.FetchTransportData(req, errorHandler) as TransportData;
    return Block.ListFromTransportData(tdResponse);
  }

    public static async FetchEntireListByDistrictRefs(district_ids: number [], errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new BlockWithFetchRequest();
    req.district_ids = district_ids;
    let tdResponse = await Block.FetchTransportData(req, errorHandler) as TransportData;
    return Block.ListFromTransportData(tdResponse);
  }

      // get all asha for Trasaction filter service file 
    public static async FetchEntireListForTransactionFilter(errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
      let req = new BlockWithFetchRequest();
      let tdResponse = await Block.FetchTransportDataForTransactionFilter(req, errorHandler) as TransportData;
      return Block.ListFromTransportData(tdResponse);
    }
  

   public static async FetchEntireListAllFilters(zone_id: number, district_id: number, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new BlockWithFetchRequest();
    if(zone_id){
      req.zone_ids.push(zone_id)
    }
    if(district_id){
      req.district_ids.push(district_id)
    }
    let tdResponse = await Block.FetchTransportData(req, errorHandler) as TransportData;
    return Block.ListFromTransportData(tdResponse);
  }

  public async DeleteInstance(successHandler: () => Promise<void> = null!, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let tdRequest = new TransportData();
    tdRequest.RequestType = RequestTypes.Deletion;

    this.MergeIntoTransportData(tdRequest);
    let pktRequest = PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);

    let tr = await ServerCommunicatorService.GetInstance().sendHttpRequest(pktRequest);
    if (!tr.Successful) {
      if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
    }
    else {
      if (!isNullOrUndefined(successHandler)) await successHandler();
    }
  }

}
